// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Dataset } from '../data';
import { stateColour } from '../data';
import type { Postcode } from '../types';
import { FUEL_COLOUR, FUEL_LABEL, FUEL_ORDER, type Fuel } from '../fuels';
import { fuelDonut } from './charts';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';
import { median, pcEvShare, pcShare, rankable, rankedBy } from '../analysis';

/** Per-postcode drill-down drawer. Hash-linkable (#postcode=3000). */
export function createDrilldown(data: Dataset): { open: (pc: string) => void; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'overlay';

  const drawer = document.createElement('div');
  drawer.className = 'drawer';
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  drawer.innerHTML = '<div class="drawer-head"></div><div class="drawer-body"></div>';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '✕';
  drawer.appendChild(closeBtn);

  document.body.append(overlay, drawer);

  const rankedBev = rankedBy(data.postcodes, 'bev');
  const rated = rankable(data.postcodes);
  const nationalBevMed = median(rated.map((p) => pcShare(p, 'bev') as number));

  const close = () => {
    overlay.classList.remove('open');
    drawer.classList.remove('open');
    if (location.hash.startsWith('#postcode=')) history.replaceState(null, '', location.pathname + location.search);
  };

  const open = (pc: string) => {
    const p = data.byPostcode.get(pc);
    if (!p) return;
    render(p);
    overlay.classList.add('open');
    drawer.classList.add('open');
    drawer.scrollTop = 0;
    closeBtn.focus();
    history.replaceState(null, '', `#postcode=${pc}`);
  };

  function render(p: Postcode) {
    const head = drawer.querySelector('.drawer-head') as HTMLElement;
    const body = drawer.querySelector('.drawer-body') as HTMLElement;

    const bevShare = pcShare(p, 'bev');
    const dieselShare = pcShare(p, 'diesel');
    const ev = pcEvShare(p);
    const rank = rankedBev.findIndex((x) => x.pc === p.pc);
    const stateRated = rankedBev.filter((x) => x.st === p.st);
    const stateRank = stateRated.findIndex((x) => x.pc === p.pc);

    head.innerHTML = `
      <div class="drawer-title">${escapeHtml(p.pc)}</div>
      <div class="drawer-sub">
        ${p.locs.length ? escapeHtml(p.locs.slice(0, 4).join(', ')) : 'No locality on record'}
        ${p.locs.length > 4 ? ` +${p.locs.length - 4} more` : ''}
        ${p.st ? ` · <span class="pill" style="background:${stateColour(p.st)}">${escapeHtml(p.st)}</span>` : ''}
      </div>
    `;

    const parts: string[] = [];

    parts.push(`
      <div class="drawer-section">
        <div class="kv-grid">
          <div class="kv"><div class="kv-label">Passenger vehicles</div><div class="kv-value">${formatNumber(p.total)}</div></div>
          <div class="kv"><div class="kv-label">Electric</div><div class="kv-value" style="color:${FUEL_COLOUR.bev}">${formatPercent(bevShare)}</div></div>
          <div class="kv"><div class="kv-label">Electrified</div><div class="kv-value" style="color:${FUEL_COLOUR.hybrid}">${formatPercent(ev)}</div></div>
          <div class="kv"><div class="kv-label">Diesel</div><div class="kv-value" style="color:${FUEL_COLOUR.diesel}">${formatPercent(dieselShare)}</div></div>
        </div>
      </div>
    `);

    // Fuel mix donut + legend.
    parts.push(`
      <div class="drawer-section" data-role="donut">
        <h3>Fuel mix</h3>
        <div style="display:flex;gap:var(--space-lg);align-items:center;flex-wrap:wrap">
          <div data-role="donut-svg" style="flex-shrink:0"></div>
          <div class="legend" style="flex-direction:column;gap:4px;margin:0" data-role="donut-legend"></div>
        </div>
      </div>
    `);

    // Rank + comparison.
    if (bevShare != null && p.total >= 500) {
      const maxCompare = Math.max(bevShare, nationalBevMed, 0.5) * 1.15;
      const row = (label: string, v: number, colour: string) => `
        <div class="compare-row">
          <div class="compare-label">${escapeHtml(label)}</div>
          <div class="compare-track"><div class="compare-fill" style="width:${Math.min(100, (v / maxCompare) * 100).toFixed(1)}%;background:${colour}"></div></div>
          <div class="compare-val">${formatPercent(v)}</div>
        </div>`;
      parts.push(`
        <div class="drawer-section">
          <h3>Electric share vs the nation</h3>
          ${row(p.pc, bevShare, FUEL_COLOUR.bev)}
          ${row('National median', nationalBevMed, '#94a3b8')}
          <p style="font-size:var(--font-size-sm);color:var(--text-secondary);margin-top:var(--space-sm)">
            Ranked <strong>#${rank + 1}</strong> of ${formatNumber(rankedBev.length)} ranked postcodes on electric share${
              p.st && stateRank >= 0 ? `, <strong>#${stateRank + 1}</strong> of ${formatNumber(stateRated.length)} in ${escapeHtml(p.st)}` : ''
            }.
          </p>
        </div>
      `);
    } else {
      parts.push(`<div class="drawer-section"><p class="empty">Too few vehicles here to rank reliably (${formatNumber(p.total)}).</p></div>`);
    }

    parts.push(`
      <div class="drawer-section">
        <h3>Every fuel type</h3>
        <div class="mix-bar tall" data-role="mixbar"></div>
        <table class="data" style="margin-top:var(--space-md);font-size:var(--font-size-sm)">
          <tbody>
            ${FUEL_ORDER.map((f) => {
              const v = p[f];
              const sh = p.total > 0 ? (v / p.total) * 100 : 0;
              return `<tr style="cursor:default"><td><span class="legend-swatch" style="display:inline-block;background:${FUEL_COLOUR[f]};margin-right:6px;vertical-align:middle"></span>${FUEL_LABEL[f]}</td><td class="t-num">${formatNumber(v)}</td><td class="t-num">${formatPercent(sh)}</td></tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    `);

    body.innerHTML = parts.join('');

    // Donut + legend.
    const donutSlot = body.querySelector('[data-role="donut-svg"]') as HTMLElement;
    donutSlot.appendChild(fuelDonut(p, FUEL_ORDER, { size: 150 }));
    const legend = body.querySelector('[data-role="donut-legend"]') as HTMLElement;
    legend.innerHTML = FUEL_ORDER.filter((f) => p[f] > 0)
      .map((f: Fuel) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${FUEL_COLOUR[f]}"></span>${FUEL_LABEL[f]} ${formatPercent(p.total > 0 ? (p[f] / p.total) * 100 : 0)}</span>`)
      .join('');

    // Stacked mini-bar.
    const mixbar = body.querySelector('[data-role="mixbar"]') as HTMLElement;
    mixbar.innerHTML = FUEL_ORDER.filter((f) => p[f] > 0)
      .map((f) => `<div class="mix-seg" style="flex:${p[f]};background:${FUEL_COLOUR[f]}" data-tip="${escapeHtml(`${FUEL_LABEL[f]}: ${formatNumber(p[f])} (${formatPercent(p.total > 0 ? (p[f] / p.total) * 100 : 0)})`)}"></div>`)
      .join('');
  }

  overlay.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) close();
  });

  return { open, close };
}
